import type { Theme } from '@mui/material';

type Breakpoint = keyof Theme['breakpoints']['values'];
type VwRange = Breakpoint | `${Breakpoint}-${Breakpoint}`;

export const createClamp =
  (theme: Theme) =>
  (minPx: number, maxPx: number, vwRange: VwRange = 'sm-4k'): string => {
    if (minPx > maxPx) {
      throw new Error(
        `[clamp] minPx (${minPx}) cannot be greater than maxPx (${maxPx})`,
      );
    }

    const toRem = (px: number) => +(px / 16).toFixed(3);
    const resolve = (bp: Breakpoint): number => theme.breakpoints.values[bp];

    const [a, b] = vwRange.split('-') as [Breakpoint, Breakpoint];
    const v1 = resolve(a);
    const v2 = resolve(b);

    if (v1 === v2) {
      throw new Error(
        `[clamp] min and max breakpoints must differ (got ${a}-${b})`,
      );
    }

    const minVw = Math.min(v1, v2);
    const maxVw = Math.max(v1, v2);
    const slope = (maxPx - minPx) / (maxVw - minVw);
    const constant = +(maxPx - slope * maxVw).toFixed(3);
    const vw = +(100 * slope).toFixed(2);

    const formatRem = (val: number) => `${toRem(val)}rem`;

    return `clamp(${formatRem(minPx)}, ${formatRem(constant)} + ${vw}vw, ${formatRem(maxPx)})`;
  };
